import { useMutation, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import { Breadcrumb, Button, MenuButton, Modal } from "../../../components/ui";
import {
  BATCH_UPDATE_ARTWORK as batchUpdateArtworkMutation,
  DELETE_ARTWORK,
  REPROCESS_IMAGES,
} from "../../../graphql/mutation/Artwork";
import { FETCH_ARTWORK as fetchArtwork } from "../../../graphql/query/Artwork";
import { FETCH_LIST as fetchList } from "../../../graphql/query/List";
import useUser from "../../../hooks/useUser";
import routes from "../pages/Wizard/routes";
import AddOffersBody from "./Actions/AddOffersBody";
import AdhocReportBody from "./Actions/AdhocReportBody";
import BatchEditBody from "./Actions/BatchEditBody";
import CopyDescriptionBody from "./Actions/CopyDescriptionBody";
import exportModule from "./Actions/ExportModule";
import ArtworkSearch from "./ArtworkSearch";
import DuplicateArtwork from "./DuplicateArtwork";
import NewArtwork from "./NewArtwork";
import { artworkFilterStore } from "./useArtworkFilters";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const search = artworkFilterStore.get();
  const pathnames = location?.pathname.split("/");
  const artworkId = pathnames?.length > 2 ? pathnames[2] : null; // get id from url

  const { data: { artwork } = {} } = useQuery(
    fetchArtwork({
      artists: { contact: {} },
      artistDescription: {},
      editions: { artwork: {}, instances: {}, availableNumbers: {}, type: {} },
      fullDescription: {},
      pressDescription: {},
      images: { imgT: {}, imgX: {}, photoMaterial: {} },
      imagesSummary: { imgT: {}, imgX: {}, photoMaterial: {} },
    }),
    {
      skip: !artworkId,
      variables: { id: +artworkId },
    },
  );

  // find current submodule route
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const imageListIdParam = queryParams.get("imageListId");
  const listId = listIdParam ? +listIdParam : null;
  const imageListId = imageListIdParam ? +imageListIdParam : null;
  const listContextQuery = [
    listIdParam ? `listId=${listIdParam}` : null,
    imageListIdParam ? `imageListId=${imageListIdParam}` : null,
  ]
    .filter(Boolean)
    .join("&");
  const buildArtworkListUrl = nextStatus =>
    `/artworks?status=${nextStatus}${listContextQuery ? `&${listContextQuery}` : ""}`;
  const buildArtworkDetailsUrl = artworkId =>
    `${artworkId}/details?status=${status}${listContextQuery ? `&${listContextQuery}` : ""}`;

  const { ids, select, disabled } = useMemo(() => {
    let select,
      ids = [];

    if (status === "imageSelectListWorking") {
      ids = Object.entries(formik.values.selectedImages?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.artwork?.id;
        },
      );
      select = formik?.values?.selectedImages?.select;
    } else {
      ids = Object.entries(formik.values.selected?.ids || {}).map(
        ([_id, value]) => {
          return value?.item?.id || value?.id;
        },
      );
      select = formik?.values?.selected?.select;
    }

    return {
      ids,
      select,
      disabled: ids?.length === 0,
    };
  }, [formik.values, status]);

  // Fetch list names when viewing saved lists (only if listId/imageListId is explicitly in URL and is a valid number)
  const { data: { list: savedList } = {} } = useQuery(fetchList(), {
    variables: { id: listId },
    skip: !listIdParam || !listId || isNaN(listId) || listId <= 0, // Only fetch if listId is explicitly in URL and valid
    errorPolicy: "ignore", // Ignore errors if list doesn't exist
  });

  const { data: { list: savedImageList } = {} } = useQuery(fetchList(), {
    variables: { id: imageListId },
    skip:
      !imageListIdParam ||
      !imageListId ||
      isNaN(imageListId) ||
      imageListId <= 0, // Only fetch if imageListId is explicitly in URL and valid
    errorPolicy: "ignore", // Ignore errors if list doesn't exist
  });

  const [
    deleteArtwork,
    {
      data: { deleteArtwork: { error: deleteArtworkError } = {} } = {},
      loading: deleteArtworkLoading,
      resetDelete,
    },
  ] = useMutation(DELETE_ARTWORK);
  const [
    batchUpdateArtwork,
    {
      data: { batchUpdateArtwork: { error: tbcArtworksError } = {} } = {},
      loading: tbcArtworksLoading,
    },
  ] = useMutation(batchUpdateArtworkMutation());
  const [reprocessImages] = useMutation(REPROCESS_IMAGES);

  const statusMap = {
    all: "All Artworks",
    searched: "Searched Artworks",
    selectListWorking:
      listIdParam && savedList?.name
        ? `Select List (${savedList.name})`
        : "Select List (Working)",
    imageSelectListWorking:
      imageListIdParam && savedImageList?.name
        ? `Image Select List (${savedImageList.name})`
        : "Image Select List (Working)",
  };
  const label = statusMap[status] || "All Artworks";
  // prepare steps for breadcrumbs
  const steps = [
    { label, onClick: () => navigate(buildArtworkListUrl(status)) },
    ...(artwork
      ? [
          {
            label: artwork?.title,
            onClick: () => navigate(buildArtworkDetailsUrl(artwork?.id)),
          },
          {
            label: route?.name,
            onClick: () => navigate(`${artwork?.id}/${route?.href}`),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const searchModalProps = {
    body: ArtworkSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // here. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: artworkId,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const deleteModalProps = {
    title: deleteArtworkError ? "Error" : "Delete Artwork",
    scale: "sm",
    description: deleteArtworkError
      ? deleteArtworkError
      : "Are you sure you wish to delete this artwork? This cannot be undone.",
    closeOnBackdrop: true,
    body: ({ closeModal }) => {
      const handleDelete = () => {
        deleteArtwork({
          variables: {
            input: { id: artwork?.id },
          },
          update: (cache, { data }) => {
            const { deleteArtwork: { success } = {} } = data || {};
            if (success) {
              closeModal();
              cache.evict({
                id: cache.identify({
                  __typename: "Artwork",
                  id: artworkId,
                }),
              });
              navigate("/artworks");
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={deleteArtworkError ? "Close" : "Cancel"}
            action={deleteArtworkError ? "primary" : "default"}
            onClick={() => {
              resetDelete();
              closeModal?.();
            }}
          />
          {!deleteArtworkError && (
            <Button
              label={deleteArtworkLoading ? "Deleting" : "Delete"}
              disabled={deleteArtworkLoading}
              onClick={handleDelete}
            />
          )}
        </div>
      );
    },
  };

  const tbcModalProps = {
    title: tbcArtworksError ? "Error" : "TBC Artwork",
    scale: "sm",
    description: tbcArtworksError
      ? tbcArtworksError
      : "Are you sure you wish to add TBC to selected artworks?",
    closeOnBackdrop: true,
    body: ({ closeModal }) => {
      const handleClick = () => {
        batchUpdateArtwork({
          variables: {
            input: {
              ids: +artworkId ? [+artworkId] : ids,
              dimensions: "Dimensions to be confirmed",
              medium: "Medium to be confirmed",
              title: "[Title to be confirmed]",
            },
          },
          update: (cache, { data }) => {
            const { batchUpdateArtwork: { success } = {} } = data || {};
            if (success) {
              closeModal();
              formik?.resetForm?.();
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={tbcArtworksError ? "Close" : "Cancel"}
            action={tbcArtworksError ? "primary" : "default"}
            onClick={() => {
              resetDelete();
              closeModal?.();
            }}
          />
          {!tbcArtworksError && (
            <Button
              label={tbcArtworksLoading ? "Processing" : "Confirm"}
              disabled={tbcArtworksLoading}
              onClick={handleClick}
            />
          )}
        </div>
      );
    },
  };

  const reprocessImagesModalProps = {
    title: "Reprocess Artwork Images",
    scale: "sm",
    closeOnBackdrop: true,
    description:
      "This function will reprocess any source artwork images and overwrite the transformed and resized images",
    body: ({ closeModal }) => {
      const handleClick = () => {
        reprocessImages({
          variables: {
            id: +artwork?.id,
          },
          update: (cache, { data }) => {
            const { reprocessImages: { success, error } = {} } = data || {};
            if (success) {
              closeModal();
            } else {
              console.error(error);
            }
          },
        });
        closeModal?.();
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label="Cancel"
            action="default"
            onClick={() => {
              closeModal?.();
            }}
          />
          <Button label="Confirm" onClick={handleClick} />
        </div>
      );
    },
  };

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const [isBatchEditOpen, setIsBatchEditOpen] = useState(false);

  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Artwork",
        modalProps: {
          body: NewArtwork,
          closeOnBackdrop: false,
          artwork,
          hideCloseButton: true,
          scale: "md",
          isCreateOpen,
          onOpen: () => {
            setIsCreateOpen(true);
          },
          onClose: () => {
            setIsCreateOpen(false);
          },
        },
      },
      {
        label: "Copy Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Description",
          scale: "sm",
          description: "Do you wish to copy artwork descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        label: "Copy Archive Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Archive Description",
          type: "archiveDescription",
          scale: "sm",
          description: "Do you wish to copy artwork archive descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        label: "Copy Artist Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Artist Description",
          type: "artistDescription",
          scale: "sm",
          description: "Do you wish to copy artwork artist descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        label: "Copy Press Description",
        disabled: !(artwork?.id || ids?.length),
        modalProps: {
          body: CopyDescriptionBody,
          title: "Copy Press Description",
          type: "pressDescription",
          scale: "sm",
          description: "Do you wish to copy artwork press descriptions?",
          closeOnBackdrop: true,
          ids,
          artwork,
        },
      },
      {
        disabled,
        label: "Add to Offers Select List",
        modalProps: {
          body: AddOffersBody,
          ...defaultActionProps,
        },
      },
      {
        disabled,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !artwork?.id && disabled,
        label: "Export Artwork Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Artwork",
          ids: artwork?.id ? [artwork?.id] : ids,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        disabled,
        label: "Batch Edit",
        modalProps: {
          body: BatchEditBody,
          isBatchEditOpen,
          onOpen: () => {
            setIsBatchEditOpen(true);
          },
          onClose: () => {
            setIsBatchEditOpen(false);
          },
          ...defaultActionProps,
        },
      },
      {
        disabled: !artwork?.id,
        label: "Reprocess Artwork Images",
        modalProps: reprocessImagesModalProps,
      },
      {
        disabled: !ids?.length && !artworkId,
        label: "TBC",
        modalProps: tbcModalProps,
      },
      ...(artworkId
        ? [
            {
              disabled: !hasPermission("DELETE_ARTWORK"),
              label: "Delete artwork",
              modalProps: deleteModalProps,
            },
            {
              label: "Duplicate Artwork",
              modalProps: {
                body: DuplicateArtwork,
                closeOnBackdrop: false,
                artwork,
                hideCloseButton: true,
                scale: "md",
              },
            },
          ]
        : []),
    ],
  };

  return (
    <div className="flex items-center justify-between border-b px-8 py-5">
      <Breadcrumb {...breadcrumbProps} />
      <div className="flex justify-end gap-4">
        <Modal {...searchModalProps}>
          <Button
            label="Artwork Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </div>
  );
};

export default PrimaryNav;
